import { Box, Button, Flex, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { useState } from "react";
import { crm } from "../../theme";

interface MonthCalendarProps<T> {
  items: T[];
  /** Data do item em ISO ("2026-09-28" ou com hora); vazio = vai para "Sem data". */
  getDate: (item: T) => string | null | undefined;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  onOpen: (item: T) => void;
  colorScheme?: (item: T) => string;
  emptyLabel?: string;
}

const dayKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/** Calendário mensal (domingo a sábado) com os itens agrupados por dia e lista de itens sem data. */
function MonthCalendar<T>({ items, getDate, getKey, getLabel, onOpen, colorScheme, emptyLabel = "Sem data" }: MonthCalendarProps<T>) {
  const [month, setMonth] = useState(() => new Date());
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });

  const byDate = new Map<string, T[]>();
  const undated: T[] = [];
  items.forEach((item) => {
    const day = getDate(item)?.substring(0, 10);
    if (day) byDate.set(day, [...(byDate.get(day) ?? []), item]);
    else undated.push(item);
  });

  return (
    <Box bg={crm.surface} border="1px solid" borderColor={crm.border} borderRadius={crm.radius} p="16px" boxShadow={crm.shadowSm}>
      <Flex justify="space-between" mb="14px" gap="10px" wrap="wrap">
        <Heading size="sm" color={crm.text}>{month.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</Heading>
        <Flex gap="6px">
          <Button size="xs" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</Button>
          <Button size="xs" onClick={() => setMonth(new Date())}>Hoje</Button>
          <Button size="xs" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</Button>
        </Flex>
      </Flex>
      <Box overflowX="auto">
        <SimpleGrid columns={7} minW="700px" border="1px solid" borderColor={crm.border}>
          {"dom seg ter qua qui sex sáb".split(" ").map((day) => (
            <Text key={day} p="8px" bg={crm.subtle} color={crm.textMuted} fontSize="xs" textAlign="center">{day}</Text>
          ))}
          {days.map((day) => (
            <Box key={dayKey(day)} minH="105px" border="1px solid" borderColor={crm.border} p="6px" color={day.getMonth() === month.getMonth() ? crm.text : crm.textFaint}>
              <Text fontSize="xs" textAlign="right">{day.getDate()}</Text>
              {(byDate.get(dayKey(day)) ?? []).map((item) => (
                <Button key={getKey(item)} mt="4px" h="auto" py="3px" w="100%" fontSize="10px" whiteSpace="normal" colorScheme={colorScheme?.(item) ?? "blue"} variant="outline" onClick={() => onOpen(item)}>
                  {getLabel(item)}
                </Button>
              ))}
            </Box>
          ))}
        </SimpleGrid>
      </Box>
      <Box mt="20px">
        <Text color={crm.textMuted} fontSize="sm">{emptyLabel} ({undated.length})</Text>
        <Flex gap="6px" wrap="wrap" mt="8px">
          {undated.map((item) => (
            <Button key={getKey(item)} size="xs" variant="outline" onClick={() => onOpen(item)}>{getLabel(item)}</Button>
          ))}
        </Flex>
      </Box>
    </Box>
  );
}

export default MonthCalendar;
